import type { ContractHistory } from '@shared/schemas';

import {
  type ClientCategoriesState,
  emptyClientCategories,
} from '../../_shared/domain/licence-client-categories';
import { type CsmCandidatesState, emptyCsmCandidates } from '../../_shared/domain/licence-csm';
import type { CreditsError } from '../domain/credit-error';
import type {
  CreditEvent,
  CreditsLoadStatus,
  GrantStatus,
} from '../domain/credit-ledger.model';
import type {
  ActivityMoreStatus,
  Approver,
  ListMutationKind,
  LoadStatus,
  NotifyStatus,
} from '../domain/licence-detail.model';
import type {
  LicenceActivityEvent,
  LicenceDetail,
  LicenceNotes,
  ListResponse,
  UserResponse,
} from '../domain/licence-detail.model';

/** Lifecycle of the cookie-notification mutation. */
export interface NotifyState {
  status: NotifyStatus;
  error: string | null;
}

/** Lifecycle of a user deletion from the Users tab (same shape as the notify lifecycle). */
export interface UserDeleteState {
  status: NotifyStatus;
  error: string | null;
}

/** Lifecycle of a deactivate / reactivate from the Users tab (#573). */
export interface UserArchiveState {
  status: NotifyStatus;
  error: string | null;
}

/** Lifecycle of the Edit licence mutation (same shape as the notify lifecycle). */
export interface EditState {
  status: NotifyStatus;
  error: string | null;
}

/**
 * Lifecycle of the Danger zone's status change (#582). It carries `revokedSessions`
 * so the snackbar can report what the deactivation actually cut instead of
 * promising it; 0 on a reactivation, and 0 when nobody was signed in.
 */
export interface LicenceStatusState {
  status: NotifyStatus;
  error: string | null;
  revokedSessions: number;
}

/**
 * Lifecycle of the Danger zone's deletion (#584). On success the page leaves for
 * the licences index; nothing of the deleted licence is patched in place.
 */
export interface LicenceDeleteState {
  status: NotifyStatus;
  error: string | null;
}

/** Lifecycle of a main-contacts save (#564); an add and a remove are both a save of the set. */
export interface ContactsSaveState {
  status: NotifyStatus;
  error: string | null;
}

/**
 * Lifecycle of a Lists-tab action (#577): delete (one or several), status change
 * or approval request. One at a time (each opens a dialog that waits for it) so
 * one lifecycle, tagged with what it is doing so the page words its snackbar.
 */
export interface ListMutationState {
  kind: ListMutationKind | null;
  status: NotifyStatus;
  error: string | null;
  /** The copy a duplication created (#662): the snackbar links to it. */
  copy: { id: string; name: string } | null;
}

/** Who an approval can be asked of, loaded when the dialog first opens (#577). */
export interface ApproversState {
  items: Approver[];
  status: LoadStatus;
}

/** The licence credit pool: balance + the raw ascending ledger, plus its load lifecycle. */
export interface CreditsState {
  balance: number;
  events: CreditEvent[];
  loadStatus: CreditsLoadStatus;
  /** Why the load failed; the tab distinguishes a 403 (not an admin) from an outage. */
  error: CreditsError | null;
}

/** Lifecycle of the credit-grant mutation. */
export interface GrantState {
  status: GrantStatus;
  error: CreditsError | null;
}

/**
 * The Overview's "Recent activity" feed (#566): the pages loaded so far, where the
 * next one starts, and two lifecycles; the first page (skeleton, empty, error
 * states) and the next (the spinner at the bottom of the card).
 */
export interface ActivityState {
  /**
   * The licence these events belong to. A page that lands for another licence
   * (a response still in flight when the admin moved from licence A to licence B)
   * is dropped by the reducer instead of being shown under B.
   */
  licenceId: string | null;
  items: LicenceActivityEvent[];
  /** Pass back for the next, older page; null once the last page is in. */
  nextCursor: string | null;
  status: LoadStatus;
  moreStatus: ActivityMoreStatus;
  error: string | null;
}

/**
 * The contract history card of the Overview tab (#750): the periods and the
 * journal of the contract fields. `licenceId` drops a late response, as for the
 * notes.
 */
export interface ContractHistoryState {
  licenceId: string | null;
  history: ContractHistory | null;
  status: LoadStatus;
  error: string | null;
}

/**
 * The Notes tab (#605): the licence's internal notes, their load lifecycle, and
 * the save's. `licenceId` says whose notes these are, so a response still in
 * flight when the admin moved to another licence is dropped by the reducer
 * instead of being shown there; as for the activity feed.
 */
export interface NotesState {
  licenceId: string | null;
  notes: LicenceNotes | null;
  status: LoadStatus;
  /** Why the load failed; the save's own error is on `save`. */
  error: string | null;
  save: { status: NotifyStatus; error: string | null };
}

/**
 * Redux state for the admin Licence Detail page. The slice owns the loaded data +
 * the load / notify / credits lifecycles; ephemeral UI (active tab, dialog open
 * flags, the cookie message or credit amount being typed) stays local to the page
 * and is passed as action payloads. Every field is serialisable.
 */
export interface LicenceDetailState {
  licence: LicenceDetail | null;
  users: UserResponse[];
  lists: ListResponse[];
  loadStatus: LoadStatus;
  notify: NotifyState;
  userDelete: UserDeleteState;
  userArchive: UserArchiveState;
  edit: EditState;
  licenceStatus: LicenceStatusState;
  licenceDelete: LicenceDeleteState;
  contactsSave: ContactsSaveState;
  listMutation: ListMutationState;
  approvers: ApproversState;
  /** Who the Edit licence form offers as Customer Success Manager (#583). */
  csmCandidates: CsmCandidatesState;
  /** The client categories the Edit licence form offers, and its "Add a category" (#604). */
  clientCategories: ClientCategoriesState;
  credits: CreditsState;
  grant: GrantState;
  activity: ActivityState;
  notes: NotesState;
  contractHistory: ContractHistoryState;
}

export const emptyNotify: NotifyState = {
  status: 'idle',
  error: null,
};

export const emptyUserDelete: UserDeleteState = {
  status: 'idle',
  error: null,
};

export const emptyUserArchive: UserArchiveState = {
  status: 'idle',
  error: null,
};

export const emptyEdit: EditState = {
  status: 'idle',
  error: null,
};

export const emptyLicenceStatus: LicenceStatusState = {
  status: 'idle',
  error: null,
  revokedSessions: 0,
};

export const emptyLicenceDelete: LicenceDeleteState = {
  status: 'idle',
  error: null,
};

export const emptyListMutation: ListMutationState = {
  kind: null,
  status: 'idle',
  error: null,
  copy: null,
};

export const emptyApprovers: ApproversState = {
  items: [],
  status: 'idle',
};

export const emptyCredits: CreditsState = {
  balance: 0,
  events: [],
  loadStatus: 'idle',
  error: null,
};

export const emptyContactsSave: ContactsSaveState = {
  status: 'idle',
  error: null,
};

export const emptyGrant: GrantState = {
  status: 'idle',
  error: null,
};

export const emptyActivity: ActivityState = {
  licenceId: null,
  items: [],
  nextCursor: null,
  status: 'idle',
  moreStatus: 'idle',
  error: null,
};

export const emptyNotes: NotesState = {
  licenceId: null,
  notes: null,
  status: 'idle',
  error: null,
  save: { status: 'idle', error: null },
};

export const emptyContractHistory: ContractHistoryState = {
  licenceId: null,
  history: null,
  status: 'idle',
  error: null,
};

export const initialState: LicenceDetailState = {
  licence: null,
  users: [],
  lists: [],
  loadStatus: 'idle',
  notify: emptyNotify,
  userDelete: emptyUserDelete,
  userArchive: emptyUserArchive,
  edit: emptyEdit,
  licenceStatus: emptyLicenceStatus,
  licenceDelete: emptyLicenceDelete,
  contactsSave: emptyContactsSave,
  listMutation: emptyListMutation,
  approvers: emptyApprovers,
  csmCandidates: emptyCsmCandidates,
  clientCategories: emptyClientCategories,
  credits: emptyCredits,
  grant: emptyGrant,
  activity: emptyActivity,
  notes: emptyNotes,
  contractHistory: emptyContractHistory,
};
